<template>
    <a-modal
        :visible="true"
        width="90vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="sgm-wrap"
        @cancel="$emit('close')"
    >
        <div class="sgm-modal">
            <div class="sgm-head">
                <div>
                    <h3>Grupos de vendedores</h3>
                    <div class="sgm-sub">Organize seus vendedores em grupos de acordo com suas necessidades</div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="sgm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <div v-if="!myQuoteOnly" class="sgm-tools">
                <a-select
                    :value="buyerId"
                    class="sgm-search"
                    show-search
                    allowClear
                    placeholder="Comprador"
                    :loading="loadingBuyers"
                    :options="buyerOptions"
                    :filterOption="filterOption"
                    @change="setBuyer"
                />
            </div>

            <div class="sgm-body">
                <a-row :gutter="[16, 16]">
                    <a-col :xs="24" :lg="7">
                        <div class="sgm-panel">
                            <div class="sgm-panel-title">Meus grupos</div>
                            <a-input-search
                                v-model="newGroupName"
                                placeholder="Nome do novo grupo"
                                :loading="creating"
                                @search="createGroup"
                            >
                                <a-button slot="enterButton" type="primary" icon="plus" />
                            </a-input-search>
                            <a-table
                                size="small"
                                class="sgm-table"
                                :columns="groupColumns"
                                :data-source="groups"
                                row-key="id"
                                :loading="loadingGroups"
                                :pagination="false"
                                :customRow="groupRow"
                                :locale="{ emptyText: 'Nenhum grupo cadastrado.' }"
                            >
                                <template slot="id" slot-scope="text">#{{ text }}</template>
                                <template slot="nome" slot-scope="text, group">
                                    <a-input
                                        v-if="editingGroupId === group.id"
                                        v-model="editingName"
                                        size="small"
                                        @click.native.stop
                                        @pressEnter="saveGroupName(group)"
                                    />
                                    <span v-else :class="{ 'sgm-strong': selectedGroup && selectedGroup.id === group.id }">{{ group.nome }}</span>
                                </template>
                                <template slot="acoes" slot-scope="text, group">
                                    <ActionMenu :actions="getGroupActions(group)" />
                                </template>
                            </a-table>
                        </div>
                    </a-col>

                    <a-col :xs="24" :lg="8">
                        <div class="sgm-panel">
                            <div class="sgm-panel-title">
                                {{ selectedGroup ? selectedGroup.nome : "Selecione um grupo" }}
                            </div>
                            <a-input-search
                                v-model="groupSellerSearch"
                                placeholder="Buscar fornecedor no grupo"
                                :disabled="!selectedGroup"
                                @search="loadGroupSellers"
                            />
                            <a-table
                                size="small"
                                class="sgm-table"
                                :columns="groupSellerColumns"
                                :data-source="groupSellers"
                                row-key="usu_id"
                                :loading="loadingGroupSellers"
                                :pagination="false"
                                :locale="{ emptyText: selectedGroup ? 'Nenhum vendedor neste grupo.' : 'Selecione um grupo para ver os vendedores.' }"
                            >
                                <template slot="fornecedor" slot-scope="text, seller">
                                    <span class="sgm-provider">{{ seller.provider ? seller.provider.for_nome : seller.for_nome }}</span>
                                </template>
                                <template slot="acoes" slot-scope="text, seller">
                                    <a-tooltip title="Remover do grupo">
                                        <a-button size="small" class="sgm-icon-btn" :loading="!!seller.loading" @click="removeSeller(seller)">
                                            <span class="material-icons-outlined sgm-danger">delete</span>
                                        </a-button>
                                    </a-tooltip>
                                </template>
                            </a-table>
                        </div>
                    </a-col>

                    <a-col :xs="24" :lg="9">
                        <div class="sgm-panel">
                            <div class="sgm-panel-title">Fornecedores disponíveis</div>
                            <a-input-search
                                v-model="providerSearch"
                                placeholder="Buscar fornecedor"
                                @search="loadSellers(1)"
                            />
                            <a-table
                                size="small"
                                class="sgm-table"
                                :columns="availableColumns"
                                :data-source="availableSellers"
                                row-key="usu_id"
                                :loading="loadingSellers"
                                :pagination="sellersPagination"
                                :locale="{ emptyText: 'Nenhum fornecedor disponível.' }"
                                @change="(p) => loadSellers(p.current)"
                            >
                                <template slot="fornecedor" slot-scope="text, seller">
                                    <span class="sgm-provider">{{ seller.for_nome }}</span>
                                </template>
                                <template slot="acoes" slot-scope="text, seller">
                                    <a-tooltip :title="selectedGroup ? 'Adicionar ao grupo' : 'Selecione um grupo'">
                                        <a-button
                                            size="small"
                                            class="sgm-icon-btn"
                                            :disabled="!selectedGroup"
                                            :loading="!!seller.loading"
                                            @click="addSeller(seller)"
                                        >
                                            <span class="material-icons-outlined sgm-success">add</span>
                                        </a-button>
                                    </a-tooltip>
                                </template>
                            </a-table>
                        </div>
                    </a-col>
                </a-row>
            </div>

            <div class="sgm-foot">
                <a-button @click="$emit('close')">Fechar</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import SellersService from "@/services/v2/sellers.service";
import SellerGroupService from "@/services/v2/seller-group.service";
import QuotationService from "@/services/QuotationService";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import { perm_mixin } from "@/mixins/permission.mixin";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";

const providerName = (seller) => (seller.provider ? seller.provider.for_nome : seller.for_nome) || "";

export default {
    name: "SellerGroupsV2Modal",
    mixins: [loaderMixin, perm_mixin],
    components: { ActionMenu },
    data() {
        return {
            service: new SellersService(),
            group_svc: new SellerGroupService(),
            quotation_service: new QuotationService(),
            myQuoteOnly: false,
            buyers: [],
            buyerId: undefined,
            loadingBuyers: false,
            groups: [],
            loadingGroups: false,
            selectedGroup: null,
            editingGroupId: null,
            editingName: "",
            newGroupName: "",
            creating: false,
            groupSellers: [],
            groupSellerSearch: "",
            loadingGroupSellers: false,
            sellers: [],
            providerSearch: "",
            loadingSellers: false,
            sellersPagination: {
                current: 1,
                pageSize: 10,
                total: 0,
                showSizeChanger: false,
                size: "small",
            },
            groupColumns: [
                { title: "#ID", dataIndex: "id", key: "id", width: 70, scopedSlots: { customRender: "id" } },
                { title: "Nome", dataIndex: "nome", key: "nome", scopedSlots: { customRender: "nome" } },
                { title: "", key: "acoes", align: "center", width: 50, scopedSlots: { customRender: "acoes" } },
            ],
            groupSellerColumns: [
                { title: "Fornecedor", key: "fornecedor", scopedSlots: { customRender: "fornecedor" } },
                { title: "Vendedor", dataIndex: "usu_nome", key: "usu_nome" },
                { title: "", key: "acoes", align: "center", width: 50, scopedSlots: { customRender: "acoes" } },
            ],
            availableColumns: [
                { title: "Fornecedor", key: "fornecedor", scopedSlots: { customRender: "fornecedor" } },
                { title: "Vendedor", dataIndex: "usu_nome", key: "usu_nome" },
                { title: "", key: "acoes", align: "center", width: 50, scopedSlots: { customRender: "acoes" } },
            ],
        };
    },
    computed: {
        buyerOptions() {
            return this.buyers.map((b) => ({ value: b.usu_id, label: b.usu_nome }));
        },
        ownerParams() {
            return !this.myQuoteOnly && this.buyerId != null ? { owner_id: this.buyerId } : {};
        },
        availableSellers() {
            const inGroup = this.groupSellers.map((s) => String(s.usu_id));
            return this.sellers.filter((s) => !inGroup.includes(String(s.usu_id)));
        },
    },
    mounted() {
        this.myQuoteOnly = (this.perms || []).some((p) => p.action === "READ" && p.resource === "MY_QUOTE");
        if (this.myQuoteOnly) this.buyerId = localStorage.getItem("usu_id");
        else this.loadBuyers();
        this.loadGroups();
        this.loadSellers(1);
    },
    methods: {
        filterOption(input, option) {
            const label = option.componentOptions.children[0].text || "";
            return label.toLowerCase().includes(input.toLowerCase());
        },
        sortByProvider(list) {
            return [...list].sort((a, b) => providerName(a).localeCompare(providerName(b)));
        },
        groupRow(group) {
            return {
                class: { "sgm-row-selected": this.selectedGroup && this.selectedGroup.id === group.id },
                on: { click: () => this.selectGroup(group) },
            };
        },
        loadBuyers() {
            this.loadingBuyers = true;
            this.quotation_service
                .getBuyers(localStorage.getItem("cli_id"))
                .then(({ data }) => {
                    this.buyers = (data.usuarios || []).map((u) => ({
                        usu_id: u.usu_id,
                        usu_nome: u.usu_nome || u.nome || (u.usuario && u.usuario.nome),
                    }));
                    const loggedUserId = Number(localStorage.getItem("usu_id"));
                    const buyer = this.buyers.find((b) => Number(b.usu_id) === loggedUserId);
                    if (buyer) this.setBuyer(buyer.usu_id);
                })
                .finally(() => {
                    this.loadingBuyers = false;
                });
        },
        setBuyer(value) {
            this.buyerId = value;
            this.clearSelection();
            this.loadGroups();
        },
        clearSelection() {
            this.selectedGroup = null;
            this.editingGroupId = null;
            this.groupSellers = [];
            this.groupSellerSearch = "";
        },
        loadGroups() {
            this.loadingGroups = true;
            return this.group_svc
                .all(this.ownerParams)
                .then(({ data }) => {
                    this.groups = Array.isArray(data) ? data : data.data || [];
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loadingGroups = false;
                });
        },
        loadSellers(page = this.sellersPagination.current) {
            this.loadingSellers = true;
            return this.service
                .all({ page, provider_name: this.providerSearch })
                .then(({ data }) => {
                    this.sellers = data.data || [];
                    this.sellersPagination.total = data.total || 0;
                    this.sellersPagination.pageSize = data.per_page || this.sellersPagination.pageSize;
                    this.sellersPagination.current = page;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loadingSellers = false;
                });
        },
        selectGroup(group) {
            if (this.selectedGroup && this.selectedGroup.id === group.id) return;
            this.selectedGroup = group;
            this.editingGroupId = null;
            this.groupSellerSearch = "";
            this.loadGroupSellers();
        },
        loadGroupSellers() {
            if (!this.selectedGroup) return;
            this.loadingGroupSellers = true;
            const params = { ...this.ownerParams };
            if (this.groupSellerSearch) params.search_name = this.groupSellerSearch;
            return this.group_svc
                .load(this.selectedGroup.id, params)
                .then(({ data }) => {
                    this.groupSellers = this.sortByProvider(data.sellers || []);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loadingGroupSellers = false;
                });
        },
        createGroup() {
            const nome = (this.newGroupName || "").trim();
            if (!nome) return;
            if (!this.myQuoteOnly && this.buyerId == null) {
                this.snack("Selecione um comprador antes de criar o grupo.", "error");
                return;
            }
            this.creating = true;
            this.group_svc
                .save({ nome, ...this.ownerParams })
                .then(({ data }) => {
                    this.newGroupName = "";
                    return this.loadGroups().then(() => {
                        const created = this.groups.find((g) => g.id === data.id);
                        if (created) this.selectGroup(created);
                    });
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.creating = false;
                });
        },
        getGroupActions(group) {
            const editing = this.editingGroupId === group.id;
            return [
                editing
                    ? { label: "Salvar nome", icon: "save", iconClass: "action-success", onClick: () => this.saveGroupName(group) }
                    : { label: "Renomear grupo", icon: "edit", iconClass: "action-info", onClick: () => this.startEditing(group) },
                { divider: true },
                { label: "Excluir grupo", icon: "delete", iconClass: "action-danger", onClick: () => this.removeGroup(group) },
            ];
        },
        startEditing(group) {
            this.editingGroupId = group.id;
            this.editingName = group.nome;
        },
        saveGroupName(group) {
            const nome = (this.editingName || "").trim();
            if (!nome) return;
            this.group_svc
                .update({ ...group, nome })
                .then(() => {
                    group.nome = nome;
                    this.editingGroupId = null;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        removeGroup(group) {
            this.confirm_action({
                message: `Confirma exclusão do grupo ${group.nome}?`,
                callback: () =>
                    this.group_svc
                        .remove(group.id, this.ownerParams)
                        .then(() => {
                            if (this.selectedGroup && this.selectedGroup.id === group.id) this.clearSelection();
                            return this.loadGroups();
                        })
                        .catch((error) => {
                            ErrorHandlerService.handle(error, this.$store);
                        }),
            });
        },
        addSeller(seller) {
            if (!this.selectedGroup) return;
            this.$set(seller, "loading", true);
            this.group_svc
                .add_to_group(this.selectedGroup.id, { seller_id: seller.usu_id, ...this.ownerParams })
                .then(() => {
                    this.groupSellers = this.sortByProvider([
                        ...this.groupSellers,
                        { ...seller, loading: false, provider: { for_nome: seller.for_nome } },
                    ]);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.$set(seller, "loading", false);
                });
        },
        removeSeller(seller) {
            this.$set(seller, "loading", true);
            this.group_svc
                .remove_from_group(this.selectedGroup.id, { seller_id: seller.usu_id, ...this.ownerParams })
                .then(() => {
                    this.groupSellers = this.groupSellers.filter((s) => s.usu_id != seller.usu_id);
                    if (!this.sellers.some((s) => s.usu_id == seller.usu_id)) {
                        this.sellers = this.sortByProvider([
                            ...this.sellers,
                            { ...seller, loading: false, for_nome: providerName(seller) },
                        ]);
                    }
                })
                .catch((error) => {
                    this.$set(seller, "loading", false);
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.sgm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.sgm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.sgm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.sgm-x.ant-btn,
.sgm-x.ant-btn:focus,
.sgm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.sgm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.sgm-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.sgm-search {
    flex: 1 1 240px;
    max-width: 360px;
}
.sgm-body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 20px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.sgm-panel {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
}
.sgm-panel-title {
    font-weight: 600;
    font-size: 14px;
    color: #262626;
    min-height: 22px;
}
.sgm-table {
    max-height: 55vh;
    overflow-y: auto;
}
.sgm-table ::v-deep .ant-table-tbody > tr {
    cursor: pointer;
}
.sgm-table ::v-deep .sgm-row-selected > td {
    background: var(--primary-color-opacity, rgba(232, 96, 44, 0.08)) !important;
}
.sgm-strong {
    font-weight: 600;
    color: var(--primary-color);
}
.sgm-provider {
    font-weight: 600;
    color: var(--primary-color);
}
.sgm-icon-btn.ant-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    box-shadow: none;
    background: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.sgm-danger {
    color: #ff4d4f;
    font-size: 18px;
}
.sgm-success {
    color: #30aa4c;
    font-size: 18px;
}
.sgm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.sgm-wrap {
    .ant-modal {
        min-width: 560px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
